import React, { useEffect, useState } from "react";

import ViewContainer from "../../../../../components/layout/view/ViewContainer";
import ViewHeader from "../../../../../components/layout/view/ViewHeader";
import EmptyState from "../../../../../components/feedback/EmptyState";
import Button from "../../../../../components/buttons/Button";

import * as groupService from "../../services/memberGroupsService";

import "./GroupApprovedMembersView.css";

const PAGE_LIMIT = 100;

const getErrorMessage = (error) =>
  error?.response?.data?.message || error?.message || "Unable to load members.";

export default function GroupApprovedMembersView({ groupTag }) {
  const [members, setMembers] = useState([]);
  const [memberCount, setMemberCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!groupTag) {
      setMembers([]);
      setMemberCount(0);
      setLoading(false);
      return;
    }

    let active = true;

    const loadMembers = async () => {
      setLoading(true);
      setError("");

      const [membersResult, countResult] = await Promise.allSettled([
        groupService.getApprovedGroupMemberships({
          groupTag,
          limit: PAGE_LIMIT,
          offset: 0,
        }),
        groupService.getApprovedGroupMembershipsCount(groupTag),
      ]);

      if (!active) return;

      if (membersResult.status === "fulfilled") {
        const data = membersResult.value?.data;

        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.members)
            ? data.members
            : Array.isArray(data?.memberships)
              ? data.memberships
              : [];

        setMembers(list);
      } else {
        setMembers([]);
        setError(getErrorMessage(membersResult.reason));
      }

      if (countResult.status === "fulfilled") {
        const count = Number(countResult.value?.data?.approvedMemberCount ?? 0);

        setMemberCount(Number.isFinite(count) && count >= 0 ? count : 0);
      }

      setLoading(false);
    };

    void loadMembers();

    return () => {
      active = false;
    };
  }, [groupTag, retry]);

  return (
    <ViewContainer>
      <section className="group-approved-members">
        <ViewHeader
          title={`Members (${memberCount})`}
          description="Approved members of this group."
        />

        {loading ? (
          <p className="group-approved-members__loading" role="status">
            Loading members...
          </p>
        ) : error ? (
          <div className="group-approved-members__error">
            <EmptyState title="Unable to load members" description={error} />

            <Button
              value="Try Again"
              variant="primary-outlined"
              onClick={() => setRetry((current) => current + 1)}
            />
          </div>
        ) : members.length === 0 ? (
          <EmptyState
            title="No members found"
            description="Approved members will appear here."
          />
        ) : (
          <div className="group-approved-members__list">
            {members.map((member) => (
              <div
                key={member.memberId}
                className="group-approved-members__item"
              >
                <div className="group-approved-members__avatar">
                  {String(member.fullName || "?")
                    .trim()
                    .charAt(0)
                    .toUpperCase()}
                </div>

                <div className="group-approved-members__info">
                  <strong className="group-approved-members__name">
                    {member.fullName || "Group Member"}
                  </strong>

                  {member.reasonForJoining && (
                    <p className="group-approved-members__reason">
                      {member.reasonForJoining}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </ViewContainer>
  );
}
